"use client";

import React, { useEffect, useState } from "react";
import { Calendar, User, Clock, ArrowRight, Share2, Tag } from "lucide-react";
import { PostsProvider, usePosts, type Post } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { cn } from "@/lib/utils";


declare const wvcClient: any;



function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="1420" className="grid grid-cols-1 gap-8 md:grid-cols-3">
        {[1, 2, 3].map((_, i) => (
          <div key={i} data-index={i} className="space-y-4">
            <div className="bg-muted/50 h-48 w-full animate-pulse rounded-xl" />
            <div className="bg-muted/50 h-6 w-3/4 animate-pulse rounded" />
            <div className="bg-muted/50 h-4 w-1/2 animate-pulse rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) {
    return null; 
  }

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="group overflow-hidden border-none shadow-none bg-transparent">
          <div className="overflow-hidden rounded-xl shadow-sm group-hover:shadow-md transition-shadow duration-300">
            <AspectRatio ratio={16 / 10}>
              {post.featuredImage ? (
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              ) : (
                <div className="bg-muted flex h-full w-full items-center justify-center text-muted-foreground">
                  <span className="text-sm font-sans">WealthCraft Insight</span>
                </div>
              )}
            </AspectRatio>
          </div>
          <CardHeader className="px-0 pt-4 pb-2">
            <div className="flex items-center gap-2 text-xs text-primary font-medium tracking-wide mb-2 uppercase">
              <Calendar className="h-3 w-3" />
              <span>{post.date ? new Date(post.date).toLocaleDateString() : "New Insight"}</span>
            </div>
            <CardTitle className="text-xl font-serif leading-tight group-hover:text-primary transition-colors">
              <Link to={post.link || "#"}>{post.title}</Link>
            </CardTitle>
          </CardHeader>
          <CardFooter className="px-0 pt-0">
            <Link to={post.link || "#"} className="text-primary flex items-center gap-1 text-sm font-bold hover:gap-2 transition-all">
              Read Insight <ArrowRight className="h-3 w-3" />
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPostsWrapper({ currentPost }: { currentPost: Post }) {
  
  const categoryIds = currentPost.categories?.map((c) => c.id) || [];
  
  const relatedQueryArgs = {
    post_type: "post",
    posts_per_page: 3,
    post__not_in: [parseInt(currentPost.id)], 
    ...(categoryIds.length > 0 && { category__in: categoryIds }), 
    orderby: "date",
    order: "DESC",
  };

  const wp_query = new WP_Query(relatedQueryArgs);

  return (
    <div className="mt-16 md:mt-24">
      <div className="mb-8 flex items-center justify-between border-b border-border pb-6">
        <h3 className="text-2xl font-serif font-bold md:text-4xl text-foreground">Related Wealth Strategies</h3>
        <Link to="/CreativeWealthPage">
          <Button variant="outline" className="hidden md:flex border-primary/20 text-primary hover:bg-primary/5">Explore All Insights</Button>
        </Link>
      </div>
      <PostsProvider wp_query={wp_query}>
        <RelatedPostsConsumer />
      </PostsProvider>
      <div className="mt-8 md:hidden">
        <Link to="/CreativeWealthPage" className="w-full">
          <Button variant="outline" className="w-full border-primary/20 text-primary">Explore All Insights</Button>
        </Link>
      </div>
    </div>
  );
}



function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-20">
        <div className="space-y-8 animate-pulse">
          <div className="h-8 w-2/3 bg-muted/50 rounded" />
          <div className="h-96 w-full bg-muted/50 rounded-2xl" />
          <div className="space-y-4">
            <div className="h-4 w-full bg-muted/50 rounded" />
            <div className="h-4 w-full bg-muted/50 rounded" />
            <div className="h-4 w-3/4 bg-muted/50 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto flex min-h-[50vh] flex-col items-center justify-center px-4 text-center">
        <h2 className="text-2xl font-serif font-bold text-destructive mb-4">Connection Interrupted</h2>
        <p className="text-muted-foreground mb-6">We encountered an issue retrieving this financial insight.</p>
        <Button className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => refetch()}>Retry Connection</Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="container mx-auto flex min-h-[50vh] flex-col items-center justify-center px-4 text-center">
        <h2 className="text-2xl font-serif font-bold mb-4">Insight Not Found</h2>
        <p className="text-muted-foreground mb-6">The strategy or article you are seeking is currently unavailable.</p>
        <Link to="/HomePage">
          <Button className="bg-primary text-primary-foreground hover:bg-primary/90">Return Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="container mx-auto max-w-5xl px-4 py-12 md:py-20">
      {}
      <header className="mx-auto max-w-4xl text-center mb-10 md:mb-16">
        <div className="mb-6 flex flex-wrap items-center justify-center gap-3 text-sm text-muted-foreground uppercase tracking-widest font-semibold">
          {post.categories && post.categories.length > 0 && (
            <span className="bg-primary text-primary-foreground rounded-full px-4 py-1.5 text-xs">
              {post.categories[0].name}
            </span>
          )}
          <span className="flex items-center gap-1.5">
            <Calendar className="h-4 w-4 text-primary" />
            {post.date ? new Date(post.date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : ""}
          </span>
          <span className="flex items-center gap-1.5">
            <Clock className="h-4 w-4 text-primary" />
            4 min read
          </span>
        </div>
        
        <h1 className="text-4xl font-serif font-bold tracking-tight md:text-6xl lg:text-7xl mb-10 text-foreground leading-[1.1]">
          {post.title}
        </h1>

        {}
        {post.author && (
          <div className="flex items-center justify-center gap-4">
            <Avatar className="h-12 w-12 border-2 border-primary/20 p-0.5">
              <AvatarImage src={post.author.avatar?.["96"] || ""} alt={post.author.name} className="rounded-full" />
              <AvatarFallback className="bg-secondary"><User className="h-6 w-6 text-primary" /></AvatarFallback>
            </Avatar>
            <div className="text-left">
              <p className="text-base font-bold text-foreground leading-tight">{post.author.name}</p>
              <p className="text-xs text-primary font-semibold uppercase tracking-wider">Financial Strategist</p>
            </div>
          </div>
        )}
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 md:mb-20 overflow-hidden rounded-3xl shadow-2xl border border-border/50">
          <AspectRatio ratio={21 / 9}>
            <img
              src={post.featuredImage}
              alt={post.title}
              className="h-full w-full object-cover"
            />
          </AspectRatio>
        </div>
      )}

      {}
      <div className="mx-auto grid max-w-4xl grid-cols-1 gap-12 lg:grid-cols-[1fr_auto]">
        
        {}
        <div className="min-w-0">
          <div 
            className="prose prose-lg dark:prose-invert max-w-none 
              prose-headings:font-serif prose-headings:font-bold prose-headings:tracking-tight 
              prose-p:text-foreground/80 prose-p:leading-relaxed
              prose-a:text-primary prose-a:font-bold prose-a:no-underline hover:prose-a:underline
              prose-img:rounded-2xl prose-img:shadow-lg prose-strong:text-foreground"
            dangerouslySetInnerHTML={{ __html: post.content || "" }} 
          />
          
          {}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-14 flex flex-wrap gap-3">
              {post.tags.map((tag) => (
                <div key={tag.id} className="flex items-center gap-1.5 rounded-full bg-secondary px-4 py-2 text-sm font-medium text-secondary-foreground hover:bg-primary hover:text-primary-foreground transition-all cursor-default">
                  <Tag className="h-3.5 w-3.5" />
                  {tag.name}
                </div>
              ))}
            </div>
          )}

          <Separator className="my-14 bg-border/60" />

          {}
          {post.author && (
            <div className="flex flex-col gap-6 rounded-2xl bg-secondary/30 border border-border/40 p-10 md:flex-row md:items-center md:gap-10">
              <Avatar className="h-24 w-24 border-4 border-background shadow-md">
                <AvatarImage src={post.author.avatar?.["96"] || ""} alt={post.author.name} />
                <AvatarFallback><User className="h-10 w-10 text-muted-foreground" /></AvatarFallback>
              </Avatar>
              <div className="flex-1 space-y-3 text-center md:text-left">
                <h3 className="text-2xl font-serif font-bold text-foreground">Insights from {post.author.name}</h3>
                <p className="text-muted-foreground leading-relaxed">
                  WealthCraft strategist dedicated to creative finance solutions and empowering individuals through multi-dimensional financial literacy.
                </p>
                <div className="pt-2">
                  <Link to={post.author.link || "#"}>
                    <Button variant="link" className="h-auto p-0 text-primary font-bold text-base hover:text-primary/80">
                      More from {post.author.name}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>

        {}
        <div className="hidden lg:block lg:w-16">
          <div className="sticky top-24 flex flex-col gap-5">
            <p className="text-[10px] font-bold text-primary uppercase tracking-[0.2em] text-center mb-2">Share</p>
            <Button variant="outline" size="icon" className="rounded-full border-border hover:text-[#1877F2] hover:border-[#1877F2]/30 shadow-sm" aria-label="Share on Facebook">
              <Share2 className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" className="rounded-full border-border hover:text-[#1DA1F2] hover:border-[#1DA1F2]/30 shadow-sm" aria-label="Share on Twitter">
              <svg className="h-4 w-4 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.84 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
            </Button>
            <Button variant="outline" size="icon" className="rounded-full border-border hover:text-[#0A66C2] hover:border-[#0A66C2]/30 shadow-sm" aria-label="Share on LinkedIn">
              <svg className="h-4 w-4 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 21.227.792 22 1.771 22h20.451C23.2 22 24 21.227 24 20.451V1.729C24 .774 23.2 0 22.225 0z"/></svg>
            </Button>
          </div>
        </div>
      </div>

      {}
      <RelatedPostsWrapper currentPost={post} />
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  
  const currentWPQueryParams = typeof wvcClient !== 'undefined' 
    ? wvcClient.getCurrentWPQueryParams() 
    : { post_type: "post", p: 1 }; 

  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-background text-foreground min-h-screen">
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}